import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, useMotionValue, useTransform, animate, type PanInfo } from 'framer-motion'
import { BadgeCheck, User } from 'lucide-react'
import { resolveProfileMediaUrl } from '@/shared/lib/resolveProfileMediaUrl'

export interface CardProfile {
  userId: string
  name: string
  age: number
  city: string
  bio?: string
  photos: Array<{ url: string; thumbUrl: string }>
  interests: Array<{ id: number; title: string; emoji?: string }>
  height?: number
  education?: string
  occupation?: string
  isPhoneVerified?: boolean
}

interface SwipeCardProps {
  profile: CardProfile
  onLike: () => Promise<void>
  onSkip: () => Promise<void>
}

/** thumb → url; URL резолвится (в т.ч. `/api/public/seed/…` → база API для MAX). */
function ProfileSwipeImage({ photo }: { photo: { url: string; thumbUrl: string } }) {
  const candidates = useMemo(() => {
    const list = [photo.thumbUrl, photo.url].filter(Boolean)
    return [...new Set(list)].map((s) => resolveProfileMediaUrl(s)).filter(Boolean)
  }, [photo.thumbUrl, photo.url])

  const [idx, setIdx] = useState(0)
  const triedRef = useRef(0)

  useEffect(() => {
    setIdx(0)
    triedRef.current = 0
  }, [photo.thumbUrl, photo.url])

  const src = candidates[idx] ?? ''

  if (!src) {
    return null
  }

  return (
    <img
      src={src}
      alt=""
      className="pointer-events-none h-full w-full object-cover select-none"
      draggable={false}
      decoding="async"
      loading="eager"
      onError={() => {
        triedRef.current += 1
        if (triedRef.current >= candidates.length * 2) return
        setIdx((i) => (i + 1 < candidates.length ? i + 1 : i))
      }}
    />
  )
}

export function SwipeCard({ profile, onLike, onSkip }: SwipeCardProps) {
  const [photoIdx, setPhotoIdx] = useState(0)
  const currentPhoto = profile.photos[photoIdx]
  const x = useMotionValue(0)
  const rotate = useTransform(x, [-240, 240], [-12, 12])
  const likeTint = useTransform(x, [0, 100], [0, 0.55])
  const passTint = useTransform(x, [-100, 0], [0.55, 0])
  const likeLabel = useTransform(x, [0, 40, 120], [0, 0.35, 1])
  const passLabel = useTransform(x, [-120, -40, 0], [1, 0.35, 0])
  const committing = useRef(false)
  const suppressPhotoTapUntil = useRef(0)

  useEffect(() => {
    setPhotoIdx(0)
    x.set(0)
    committing.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps -- x.set намеренно без подписки на x
  }, [profile.userId])

  const nPhotos = profile.photos.length

  const cyclePhoto = () => {
    if (nPhotos <= 1) return
    if (typeof performance !== 'undefined' && performance.now() < suppressPhotoTapUntil.current) return
    setPhotoIdx((i) => (i + 1) % nPhotos)
  }

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (committing.current) return
    const threshold = 72
    const vx = info.velocity.x
    const ox = Math.abs(info.offset.x) > 2 ? info.offset.x : x.get()
    const goRight = ox > threshold || vx > 520
    const goLeft = ox < -threshold || vx < -520

    if (Math.abs(ox) > 12 || Math.abs(vx) > 120) {
      suppressPhotoTapUntil.current =
        (typeof performance !== 'undefined' ? performance.now() : 0) + 320
    }

    if (goRight) {
      committing.current = true
      try {
        await animate(x, 520, { duration: 0.28, ease: [0.22, 1, 0.36, 1] })
        await onLike()
      } catch {
        await animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 })
      } finally {
        committing.current = false
      }
    } else if (goLeft) {
      committing.current = true
      try {
        await animate(x, -520, { duration: 0.28, ease: [0.22, 1, 0.36, 1] })
        await onSkip()
      } catch {
        await animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 })
      } finally {
        committing.current = false
      }
    } else {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 })
    }
  }

  const initial = profile.name.trim().charAt(0) || '?'
  const cityNameLine = [profile.city?.trim(), profile.name?.trim()].filter(Boolean).join(' — ')

  return (
    <motion.div
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.92, opacity: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      style={{ x, rotate, touchAction: 'none' }}
      drag="x"
      dragElastic={0.88}
      dragMomentum={false}
      onDragEnd={handleDragEnd}
      className="relative flex h-full w-full min-h-0 cursor-grab flex-col overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5 active:cursor-grabbing dark:bg-gray-800 dark:ring-white/10"
    >
      <motion.div
        className="pointer-events-none absolute inset-0 z-20 rounded-3xl bg-emerald-500"
        style={{ opacity: likeTint }}
      />
      <motion.div
        className="pointer-events-none absolute inset-0 z-20 rounded-3xl bg-orange-500"
        style={{ opacity: passTint }}
      />

      <motion.div
        className="pointer-events-none absolute left-5 top-1/2 z-30 -translate-y-1/2 rounded-lg border-4 border-orange-600 px-3 py-1.5 text-lg font-black uppercase tracking-wider text-orange-700 shadow-sm"
        style={{ opacity: passLabel }}
      >
        Пропуск
      </motion.div>
      <motion.div
        className="pointer-events-none absolute right-5 top-1/2 z-30 -translate-y-1/2 rounded-lg border-4 border-emerald-600 px-3 py-1.5 text-lg font-black uppercase tracking-wider text-emerald-800 shadow-sm"
        style={{ opacity: likeLabel }}
      >
        Симпатия
      </motion.div>

      <div
        className={`relative min-h-[52%] shrink-0 bg-gray-900 ${nPhotos > 1 ? 'cursor-pointer' : ''}`}
        style={{ flex: '1 1 62%' }}
        onClick={cyclePhoto}
        onKeyDown={
          nPhotos > 1
            ? (ev) => {
                if (ev.key === 'ArrowLeft') {
                  ev.preventDefault()
                  setPhotoIdx((i) => (i - 1 + nPhotos) % nPhotos)
                }
                if (ev.key === 'ArrowRight') {
                  ev.preventDefault()
                  setPhotoIdx((i) => (i + 1) % nPhotos)
                }
              }
            : undefined
        }
        role={nPhotos > 1 ? 'button' : undefined}
        tabIndex={nPhotos > 1 ? 0 : undefined}
        aria-label={nPhotos > 1 ? 'Фото: нажми, чтобы листать по кругу' : undefined}
      >
        {nPhotos > 0 && currentPhoto ? (
          <div className="pointer-events-none h-full w-full">
            <ProfileSwipeImage photo={currentPhoto} />
          </div>
        ) : (
          <div className="pointer-events-none flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900 text-white/90">
            <User className="h-16 w-16 opacity-40" strokeWidth={1.25} aria-hidden />
            <span className="text-5xl font-semibold opacity-90">{initial}</span>
            <p className="max-w-[80%] text-center text-xs text-white/60">Нет фото в анкете</p>
          </div>
        )}

        {nPhotos > 1 && (
          <div
            className="absolute bottom-3 left-0 right-0 z-[16] flex justify-center gap-2 px-3"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {profile.photos.map((_, idx) => (
              <button
                key={idx}
                type="button"
                aria-label={`Фото ${idx + 1} из ${nPhotos}`}
                aria-current={idx === photoIdx ? 'true' : undefined}
                onClick={() => setPhotoIdx(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  idx === photoIdx ? 'w-5 bg-white' : 'h-1.5 w-1.5 bg-white/45 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex min-h-[38%] shrink-0 touch-pan-y flex-col justify-between gap-2 overflow-y-auto overscroll-contain p-4 pt-3">
        <div className="shrink-0 space-y-1.5">
          <h2 className="flex min-w-0 flex-wrap items-center gap-1.5 text-base font-bold leading-snug text-gray-900 dark:text-white">
            <span className="min-w-0 truncate">
              {cityNameLine}
              {profile.age > 0 ? `, ${profile.age}` : ''}
            </span>
            {profile.isPhoneVerified ? (
              <span
                className="inline-flex shrink-0 text-emerald-600 dark:text-emerald-400"
                title="Телефон подтверждён в MAX"
              >
                <BadgeCheck className="h-5 w-5" aria-hidden />
                <span className="sr-only">Телефон подтверждён в MAX</span>
              </span>
            ) : null}
          </h2>
          {profile.bio && (
            <p className="line-clamp-4 text-sm leading-snug text-gray-700 dark:text-gray-300">{profile.bio}</p>
          )}
        </div>

        {profile.interests.length > 0 && (
          <div className="shrink-0 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2 dark:border-gray-700/80">
            {profile.interests.slice(0, 3).map((interest) => (
              <span
                key={interest.id}
                className="rounded-full bg-accent/10 px-2 py-1 text-xs text-accent"
              >
                {interest.emoji} {interest.title}
              </span>
            ))}
            {profile.interests.length > 3 && (
              <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                +{profile.interests.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}
